import { Alert } from '@odin-labs/components';
import {
  useGetAvigilonAccessConnectorSetupMetadataQuery,
  useSetupAvigilonAccessConnectorMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { ArrowUpRightFromSquare } from 'components/icons';
import type { ConnectorSetupDrawerProps } from 'containers/jobsiteConfiguration/tabs/connectors/drawers/connectorCatalogEntry';
import { ConnectorSetupDrawerLayout } from 'containers/jobsiteConfiguration/tabs/connectors/drawers/shared/ConnectorSetupDrawerLayout';
import React from 'react';
import { getGraphQLError } from 'utils/error';
import { getAvigilonAccessOrganizationUrl } from './avigilonAccessUrls';

export function AvigilonAccessConnectorSetupDrawer({
  isOpen,
  siteId,
  catalogItem,
  onClose,
  onCreated,
  onBack,
}: ConnectorSetupDrawerProps): React.ReactElement {
  const metadataQuery = useGetAvigilonAccessConnectorSetupMetadataQuery({
    variables: { siteId },
    fetchPolicy: 'no-cache',
    skip: !isOpen,
  });
  const [setup, setupState] = useSetupAvigilonAccessConnectorMutation();
  const connectorDefinition = metadataQuery.data?.connectorCatalog.definition;
  const accessDefinition =
    connectorDefinition?.kind === 'AvigilonAccessConnectorDefinition'
      ? connectorDefinition
      : null;
  const organizationUrl = getAvigilonAccessOrganizationUrl(
    accessDefinition?.organizationId,
  );

  const connect = async (): Promise<void> => {
    try {
      await setup({ variables: { input: { siteId } } });
      await onCreated();
      onClose();
      AlertService.alert(
        'success',
        `${catalogItem.displayName} connected`,
        null,
      );
    } catch (submitError) {
      await onCreated();
      onClose();
      AlertService.alert(
        'danger',
        `Could not connect ${catalogItem.displayName}`,
        getGraphQLError(submitError),
      );
    }
  };

  return (
    <ConnectorSetupDrawerLayout
      isOpen={isOpen}
      title={catalogItem.displayName}
      loading={setupState.loading}
      actionEnabled={catalogItem.available}
      onAction={connect}
      onClose={onClose}
      onBack={onBack}
    >
      <div className="odin-space-y-3 odin-text-sm odin-text-gray-600">
        <p>{catalogItem.setupDescription}</p>
        {!catalogItem.available && (
          <Alert
            type="warn"
            text="Configure the Avigilon organization and credentials in the environment settings to create an Avigilon Access connector."
          />
        )}
        {organizationUrl && (
          <a
            href={organizationUrl}
            target="_blank"
            rel="noreferrer"
            className="odin-inline-flex odin-items-center odin-gap-2 odin-font-medium odin-text-odin-primary hover:odin-underline"
          >
            View Avigilon organization
            <ArrowUpRightFromSquare className="odin-h-3.5 odin-w-3.5" />
          </a>
        )}
      </div>
    </ConnectorSetupDrawerLayout>
  );
}
